import { useState } from "react";
import axios from "axios";
import { IconWithBackground } from "@/subframe/components/IconWithBackground";
import { TextField } from "@/subframe/components/TextField";
import { Button } from "@/subframe/components/Button";
import { IconButton } from "@/subframe/components/IconButton";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { addLabel } from "../../redux/reducers/authSlice";

interface LabelCreationProps {
  open: boolean;
  setIsLabelCreationOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function LabelCreation({ open, setIsLabelCreationOpen }: LabelCreationProps) {
  
  const { token } = useSelector((state: RootState) => state.auth);
  const [label, setLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const dispatch = useDispatch<AppDispatch>();

  const handleSave = async () => {
    if (!label.trim()) {
      setError("Label name cannot be empty.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        "https://api.insightssystem.com/api:uYdlVwac/web/label_management",
        {
          label,
          keywords: [],
        },
        { headers: { 'Content-Type': 'application/json', ...(token && { Authorization: token }) } }
      );

      dispatch(addLabel(response.data));
      setIsLabelCreationOpen(false);
      setLabel("");
    } catch (err) {
      setError("Failed to create label. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Don't render if modal is not open
  if (!open) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[70] transition-opacity duration-300"
        onClick={() => setIsLabelCreationOpen(false)}
      />
      
      {/* Modal Content */}
      <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-3">
              <IconWithBackground
                className="h-10 w-10 flex-none"
                size="large"
                icon="FeatherTag"
              />
              <div className="text-center">
                <h2 className="text-xl font-bold text-gray-900">Label</h2>
                <p className="text-sm text-gray-600">
                  Create a label name for categorizing conversations
                </p>
              </div>
            </div>
            <IconButton
              variant="brand-tertiary"
              size="large"
              icon="FeatherX"
              onClick={() => setIsLabelCreationOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            />
          </div>

          {/* Modal Body */}
          <div className="flex-1 p-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <TextField
                  className="w-full"
                  label=""
                  helpText=""
                >
                  <TextField.Input
                    placeholder="Enter Label"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    disabled={loading}
                    onKeyDown={(e) => e.key === "Enter" && handleSave()}
                  />
                </TextField>
              </div>

              {error && <span className="text-red-500 text-sm">{error}</span>}

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="brand-secondary"
                  onClick={() => setIsLabelCreationOpen(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={loading}>
                  {loading ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default LabelCreation;
